import { PureComponent } from 'react';
import moment from 'moment';
import { isNil } from 'lodash';
import { FormattedMessage, injectIntl } from 'react-intl';
import { fromJS } from 'immutable';

// UTILS
import { fuel as fuelUtils } from 'utils';
import { ifInTestingMode } from 'utils/tests.utils';

// HOOKS
import { useAbility } from 'permissions';

// COMPONENTS
import Checkbox from 'components/Checkbox';
import MaybeTranslation from 'components/MaybeTranslation';
import Loading from 'components/Loading';
import { RadioButton } from 'components/RadioButtons';
import Pagination from 'components/Pagination';

// STYLES
import { NoResults } from 'styles/common';
import {
  TableContainer,
  TableContent,
  THead,
  HeaderItemWrapper,
  Row,
  Column,
  TableWrapper,
  SELECTABLE,
  Screen,
} from 'styles/table';

import { HeaderItem, Icon, ColumnLink } from './Table.styles';
import { ACTIVE, HIDDEN } from 'styles/commonClasses';

// CONSTANTS
import { SORT_DIRECTION } from 'constants/table';
import { FILTER_TYPES } from 'constants/filters';
import { DATETIME_FORMAT } from 'constants/formats.constants';

const Header = (props) => {
  const { sortBy } = props;

  let sortByName = null;
  let sortByDirection = null;

  if (!isNil(sortBy)) {
    [sortByName, sortByDirection] = sortBy;
  }

  const columns = props.fields.map((item) => {
    const name = item.get('name');
    const sortActive = String(sortByName) === name;
    const sortable = item.get('sortable', true);

    let iconClass = 'icon-sort';

    if (sortActive) {
      iconClass += ` icon-sort-${String(sortByDirection)}`;
    }

    return (
      <HeaderItem
        key={`headerItem-${name}`}
        data-testid={`table__header__${name}`}
        sortable={sortable}
        sortActive={sortActive}
        onClick={sortable ? props.onSortBy(name) : undefined}
      >
        <HeaderItemWrapper>
          <MaybeTranslation renderedValue={item.get('title')} />
          {sortable && <Icon className={iconClass} data-testid={ifInTestingMode('sort-icon')} />}
        </HeaderItemWrapper>
      </HeaderItem>
    );
  });

  return (
    <THead>
      <tr>
        {props.allowMultipleSelections && props.showToggle && (
          <HeaderItem>
            <Checkbox checked={props.allSelected} onClick={props.onToggleSelectAll} />
          </HeaderItem>
        )}
        {columns}
      </tr>
    </THead>
  );
};

const TableRow = (props) => {
  const { intl } = props;
  const ability = useAbility();

  const linkToResourceAllowed = ability.can('access', props.viewTypeName);

  const handleClickLink = (e) => {
    e.stopPropagation();
  };

  const columns = props.fields.map((columnItem) => {
    const name = columnItem.get('name');
    const dataPath = columnItem.get('dataPath');
    const format = columnItem.get('type');
    const decimalDigits = columnItem.get('decimalDigits');
    const valueTranslations = columnItem.get('valueTranslations');
    const unitTranslation = columnItem.get('unitTranslation');
    const link = columnItem.get('link');
    const linkAttributePath = columnItem.get('linkAttributePath');
    const linkNewTab = columnItem.get('linkNewTab') === true;

    let rawValue;
    let renderedValue;

    if (typeof dataPath === 'function') {
      rawValue = dataPath(props.item, props.fieldContext);
    } else {
      rawValue = props.item.getIn(dataPath.split('.'));
    }

    renderedValue = rawValue;

    if (!isNil(renderedValue)) {
      if (format === 'date') {
        renderedValue = moment(renderedValue).format('L');
      }

      if (format === 'dateTime') {
        renderedValue = moment(renderedValue).format(DATETIME_FORMAT);
      }

      if (format === 'money') {
        // TODO(hjack): get currency from user settings
        renderedValue = intl.formatNumber(renderedValue, {
          style: 'currency',
          currency: 'CAD',
        });
      }

      if (format === 'fractionalFuel' && !isNil(renderedValue)) {
        renderedValue = intl.formatNumber(fuelUtils.convertIndexToPercentage(renderedValue) / 100, {
          style: 'percent',
          maximumFractionDigits: 1,
        });
      }

      if (typeof decimalDigits === 'number') {
        renderedValue = Number(renderedValue).toFixed(decimalDigits);
      }
    }

    // TODO: Table row should not be aware of filter types
    if (format === FILTER_TYPES.dropdown || format === FILTER_TYPES.fuelDropdown) {
      const translation = columnItem.getIn(['values', String(renderedValue)]);

      renderedValue = <MaybeTranslation renderedValue={translation} />;
    }

    if (valueTranslations) {
      const translationId = valueTranslations.getIn([String(renderedValue), 'translationId']);

      renderedValue = translationId ? intl.formatMessage({ id: translationId }) : renderedValue;
    }

    if (unitTranslation && renderedValue) {
      renderedValue = intl.formatMessage({ id: unitTranslation }, { val: renderedValue });
    }

    if (typeof renderedValue === 'boolean') {
      renderedValue = <FormattedMessage id={String(renderedValue)} />;
    }

    if (!isNil(props.placeholder) && isNil(renderedValue)) {
      renderedValue = props.placeholder;
    }

    if (format === 'humanizedDate') {
      if (isNil(rawValue)) {
        renderedValue = '-';
      } else {
        const target = moment(rawValue);
        const daysFromToday = target.clone().startOf('day').diff(moment().startOf('day'), 'days');

        if (daysFromToday === 1) {
          renderedValue = intl.formatMessage({ id: 'services.Vehicles.nextAgreementTomorrow' });
        } else {
          const text = target.fromNow();
          renderedValue = text.charAt(0).toUpperCase() + text.slice(1);
        }
      }
    }

    if (link) {
      const skipLink = linkAttributePath && isNil(props.item.getIn(linkAttributePath.split('.')));

      if (!skipLink) {
        const to = linkAttributePath ? `${link}/${props.item.getIn(linkAttributePath.split('.'))}` : link;
        const target = linkNewTab ? '_blank' : undefined;

        if (linkToResourceAllowed) {
          renderedValue = (
            <ColumnLink onClick={handleClickLink} target={target} to={to}>
              {renderedValue}
            </ColumnLink>
          );
        } else {
          renderedValue = <FormattedMessage id={String(renderedValue)} />;
        }
      }
    }

    return (
      <Column key={`data-${name}`} data-testid={ifInTestingMode(`table__cell__${columnItem.get('name')}`)}>
        {renderedValue}
      </Column>
    );
  });

  let selectionButton;

  if (props.allowMultipleSelections) {
    selectionButton = <Checkbox checked={props.selected} onClick={props.onToggleSelection(props.item)} />;
  } else {
    // @ts-expect-error TS(2769): No overload matches this call.
    selectionButton = <RadioButton selected={props.selected} onClick={props.onToggleSelection(props.item)} active />;
  }

  const onClick = () => {
    if (!props.disabled) {
      props.onClick(props.item);
    }
  };

  return (
    <Row
      // @ts-expect-error TS(2769): No overload matches this call.
      disabled={props.disabled}
      data-testid={ifInTestingMode('table__row')}
      className={props.className}
      onClick={onClick}
    >
      {props.showToggle && <Column>{selectionButton}</Column>}
      {columns}
    </Row>
  );
};

const Rows = (props) => {
  if (!props.data) {
    return null;
  }

  const rows = props.data.map((item, index) => {
    const className = props.focusedIndex === index ? ACTIVE : undefined;

    return (
      <TableRow
        key={`item-row-${item.get('id')}`}
        allowMultipleSelections={props.allowMultipleSelections}
        fieldContext={props.fieldContext}
        className={className}
        fields={props.fields}
        item={item}
        selected={props.selectedItems.findIndex((selectedItem) => selectedItem.get('id') === item.get('id')) >= 0}
        showToggle={props.showToggle}
        placeholder={props.placeholder}
        onToggleSelection={props.onToggleSelection}
        onClick={props.onClick}
        disabled={!props.getIsRowEnabled(item)}
        intl={props.intl}
        viewTypeName={props.viewTypeName}
      />
    );
  });

  return <tbody>{rows}</tbody>;
};

Rows.defaultProps = {
  getIsRowEnabled: () => true,
};

const { DESCENDING, ASCENDING } = SORT_DIRECTION;

class Table extends PureComponent {
  static defaultProps = {
    allowMultipleSelections: true,
    currentPage: 0,
    fieldContext: fromJS({}),
    focusedIndex: -1,
    getIsRowEnabled: undefined,
    loading: false,
    perPage: 10,
    placeholder: '',
    selectedItems: fromJS([]),
    sortBy: null,
    showHeader: true,
    showPagination: true,
    showToggle: true,
    totalPages: 1,
    onChangePerPage: () => undefined,
    onGetNextPage: () => undefined,
    onGetPage: () => undefined,
    onGetPreviousPage: () => undefined,
    onSelect: undefined,
    onSortBy: () => undefined,
    onToggleSelectAll: () => undefined,
    onToggleSelection: () => undefined,
  };

  // Marks an item as selected or unselects it if it's already selected
  handleToggleSelection = (item) => () => {
    // @ts-expect-error TS(2339): Property 'onToggleSelection' does not exist on typ... Remove this comment to see the full error message
    this.props.onToggleSelection(item);
  };

  handleRowClick = (item) => {
    // @ts-expect-error TS(2339): Property 'onToggleSelection' does not exist on typ... Remove this comment to see the full error message
    this.props.onToggleSelection(item);

    // @ts-expect-error TS(2339): Property 'onSelect' does not exist on type 'Readon... Remove this comment to see the full error message
    if (this.props.onSelect) {
      // @ts-expect-error TS(2339): Property 'onSelect' does not exist on type 'Readon... Remove this comment to see the full error message
      this.props.onSelect(item);
    }
  };

  handleOnSortBy = (newSortName) => () => {
    let previousSortName = null;
    let previousSortDirection = null;

    // @ts-expect-error TS(2339): Property 'sortBy' does not exist on type 'Readonly... Remove this comment to see the full error message
    if (!isNil(this.props.sortBy)) {
      // @ts-expect-error TS(2339): Property 'sortBy' does not exist on type 'Readonly... Remove this comment to see the full error message
      [previousSortName, previousSortDirection] = this.props.sortBy;
    }

    // If sort is not set, set it to descending
    if (previousSortName !== newSortName) {
      // @ts-expect-error TS(2339): Property 'onSortBy' does not exist on type 'Readon... Remove this comment to see the full error message
      this.props.onSortBy([newSortName, DESCENDING]);

      return;
    }

    // If sort is set to descending, set it so ascending
    if (previousSortName === newSortName && previousSortDirection === DESCENDING) {
      // @ts-expect-error TS(2339): Property 'onSortBy' does not exist on type 'Readon... Remove this comment to see the full error message
      this.props.onSortBy([newSortName, ASCENDING]);

      return;
    }

    // Third click means we remove the sort
    if (previousSortName === newSortName && previousSortDirection === ASCENDING) {
      // @ts-expect-error TS(2339): Property 'onSortBy' does not exist on type 'Readon... Remove this comment to see the full error message
      this.props.onSortBy(null);
    }
  };

  render() {
    // @ts-expect-error TS(2339): Property 'data' does not exist on type 'Readonly<{... Remove this comment to see the full error message
    const { data, fieldContext, loading, onSelect, showHeader } = this.props;

    return (
      <TableWrapper>
        <TableContainer>
          {data.size !== 0 && (
            <TableContent
              // Use styled based hiding to avoid awkward table reordering
              className={[loading ? HIDDEN : '', onSelect ? SELECTABLE : ''].join(' ')}
            >
              {showHeader && (
                <Header
                  // @ts-expect-error TS(2339): Property 'allowMultipleSelections' does not exist ... Remove this comment to see the full error message
                  allowMultipleSelections={this.props.allowMultipleSelections}
                  // @ts-expect-error TS(2339): Property 'fields' does not exist on type 'Readonly... Remove this comment to see the full error message
                  fields={this.props.fields}
                  // @ts-expect-error TS(2339): Property 'onToggleSelectAll' does not exist on typ... Remove this comment to see the full error message
                  onToggleSelectAll={this.props.onToggleSelectAll}
                  // @ts-expect-error TS(2339): Property 'sortBy' does not exist on type 'Readonly... Remove this comment to see the full error message
                  sortBy={this.props.sortBy}
                  // @ts-expect-error TS(2339): Property 'selectedItems' does not exist on type 'R... Remove this comment to see the full error message
                  allSelected={Boolean(data.size !== 0 && data.size === this.props.selectedItems.size)}
                  // @ts-expect-error TS(2339): Property 'showToggle' does not exist on type 'Read... Remove this comment to see the full error message
                  showToggle={this.props.showToggle}
                  onSortBy={this.handleOnSortBy}
                />
              )}

              <Rows
                // @ts-expect-error TS(2339): Property 'allowMultipleSelections' does not exist ... Remove this comment to see the full error message
                allowMultipleSelections={this.props.allowMultipleSelections}
                fieldContext={fieldContext}
                data={data}
                // @ts-expect-error TS(2339): Property 'fields' does not exist on type 'Readonly... Remove this comment to see the full error message
                fields={this.props.fields}
                // @ts-expect-error TS(2339): Property 'focusedIndex' does not exist on type 'Re... Remove this comment to see the full error message
                focusedIndex={this.props.focusedIndex}
                // @ts-expect-error TS(2339): Property 'getIsRowEnabled' does not exist on type ... Remove this comment to see the full error message
                getIsRowEnabled={this.props.getIsRowEnabled}
                // @ts-expect-error TS(2339): Property 'placeholder' does not exist on type 'Rea... Remove this comment to see the full error message
                placeholder={this.props.placeholder}
                // @ts-expect-error TS(2339): Property 'selectedItems' does not exist on type 'R... Remove this comment to see the full error message
                selectedItems={this.props.selectedItems}
                // @ts-expect-error TS(2339): Property 'showToggle' does not exist on type 'Read... Remove this comment to see the full error message
                showToggle={this.props.showToggle}
                onClick={this.handleRowClick}
                onToggleSelection={this.handleToggleSelection}
                // @ts-expect-error TS(2339): Property 'intl' does not exist on type 'Readonly<{... Remove this comment to see the full error message
                intl={this.props.intl}
                // @ts-expect-error TS(2339): Property 'viewTypeName' does not exist on type 'Readonly... Remove this comment to see the full error message
                viewTypeName={this.props.viewTypeName}
              />
            </TableContent>
          )}
          {!loading && data.size === 0 && (
            <Screen data-testid={ifInTestingMode('table__loading-screen')}>
              <NoResults>
                <FormattedMessage id="noResults" />
              </NoResults>
            </Screen>
          )}
          {loading && (
            <Screen data-testid={ifInTestingMode('table__loading-screen')}>
              <Loading />
            </Screen>
          )}
        </TableContainer>

        {/* @ts-expect-error TS(2339): Property 'showPagination' does not exist on type '... Remove this comment to see the full error message */}
        {this.props.showPagination && (
          <Pagination
            // @ts-expect-error TS(2339): Property 'totalPages' does not exist on type 'Read... Remove this comment to see the full error message
            total={this.props.totalPages}
            // @ts-expect-error TS(2339): Property 'currentPage' does not exist on type 'Rea... Remove this comment to see the full error message
            current={this.props.currentPage}
            // @ts-expect-error TS(2339): Property 'perPage' does not exist on type 'Readonl... Remove this comment to see the full error message
            perPage={this.props.perPage}
            // @ts-expect-error TS(2339): Property 'onGetPreviousPage' does not exist on typ... Remove this comment to see the full error message
            onGetPreviousPage={this.props.onGetPreviousPage}
            // @ts-expect-error TS(2339): Property 'onGetNextPage' does not exist on type 'R... Remove this comment to see the full error message
            onGetNextPage={this.props.onGetNextPage}
            // @ts-expect-error TS(2339): Property 'onGetPage' does not exist on type 'Reado... Remove this comment to see the full error message
            onGetPage={this.props.onGetPage}
            // @ts-expect-error TS(2339): Property 'onChangePerPage' does not exist on type ... Remove this comment to see the full error message
            onChangePerPage={this.props.onChangePerPage}
            // @ts-expect-error TS(2339): Property 'intl' does not exist on type 'Readonly<{... Remove this comment to see the full error message
            intl={this.props.intl}
          />
        )}
      </TableWrapper>
    );
  }
}

// @ts-expect-error TS(2769): No overload matches this call.
export default injectIntl(Table);
